/** Facilities / Sites - where the energy is used (ADR-0212).
 *
 * One row per site: its company, its country and grid region (which decide its factors), its lease
 * treatment (ADR-0034: an unanswered one holds every filing the site contributes to, so it is said in
 * the row), its coverage in the period (the census's own count of missing windows, never a percentage)
 * and whether it has been retired. A row opens the site's own page.
 *
 * **Admin acts, and only for an Admin.** Add site, Register many from a file (the structure file,
 * ADR-0091) and Manage (correct details, lease treatment, meters and retirement - unchanged forms in a
 * drawer) change what every figure is about, so they are drawn for an Admin and for no one else
 * (design rule 6). Everyone reads the table.
 */

import { Buildings } from "@phosphor-icons/react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import { useMe } from "../../api/auth";
import { useCoverageRollup } from "../../api/coverageRollup";
import { useFacilities } from "../../api/evidence";
import { useEntities } from "../../api/queries";
import { useOrgTree } from "../../api/vendorSummary";
import { ReadFailed } from "../../components/Loaded";
import { AddImportActions } from "../../components/ui/AddImportActions";
import { DashboardBand } from "../../components/ui/DashboardBand";
import { Drawer } from "../../components/ui/Drawer";
import { EmptyAction } from "../../components/ui/EmptyAction";
import { EmptyState } from "../../components/ui/EmptyState";
import { EntityLink } from "../../components/ui/EntityLink";
import { FilterLine } from "../../components/ui/FilterLine";
import { EMPTY_FILTERS } from "../../components/ui/filters";
import type { FilterDef, FilterState } from "../../components/ui/filters";
import { GridTable } from "../../components/ui/GridTable";
import type { GridColumn } from "../../components/ui/GridTable";
import { InfoPopover } from "../../components/ui/InfoPopover";
import type { SortState } from "../../components/ui/sortRows";
import { Skeleton } from "../../components/ui/Skeleton";
import { StatusChip } from "../../components/ui/StatusChip";
import { TablePage } from "../../components/ui/TablePage";
import { Tile, TileStrip } from "../../components/ui/Tile";
import { CoverageCard, SiteTotalsCard } from "../bands/FacilityCards";
import { FacilityForm, LeaseTreatment, SitePanel } from "../Organisation";
import { SiteCorrection } from "../OrganisationEdit";
import { LEASE_LABEL } from "../organisationOptions";
import { keepSearch } from "../search";
import type { ScreenSearch } from "../search";
import { count, matches, ordered, usePlaceLabel } from "../tableKit";
import type { Screen } from "../useScreen";
import styles from "./Facilities.module.css";
import { useReporter } from "./useReporter";

import type { FacilityRow } from "../../api/evidence";

function coverageWords(missing: number | undefined, sources: number | undefined): string {
  if (sources === undefined) return "—";
  if (sources === 0) return "No sources set up";
  return missing ? `${count(missing)} missing` : "None missing";
}

export function SitesTab({ screen }: { screen: Screen }) {
  const label = usePlaceLabel();
  const navigate = useNavigate();
  const isAdmin = useMe().data?.role === "admin";
  const facilities = useFacilities();
  const entities = useEntities();
  const window = screen.window;
  const tree = useOrgTree(window?.period_start, window?.period_end, screen.method);
  const coverage = useCoverageRollup(window?.period_start, window?.period_end, Boolean(window));
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [sort, setSort] = useState<SortState | null>(null);
  const [managing, setManaging] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const adder = useReporter(() => void facilities.refetch());
  const manager = useReporter(() => void facilities.refetch());

  const retired = new Set((tree.data?.facilities ?? []).filter((f) => f.retired).map((f) => f.facility_id));
  const siteCover = new Map((coverage.data?.sites ?? []).map((s) => [s.facility_id, s]));
  const companies = (entities.data ?? []).map((e) => ({ id: e.id, name: e.legal_name }));
  const rows = facilities.data ?? [];

  const defs: FilterDef[] = [
    {
      id: "company",
      label: "Company",
      mode: "single",
      options: [...new Set(rows.map((r) => r.legal_entity_name))].map((name) => ({ value: name, label: name })),
    },
    {
      id: "state",
      label: "State",
      mode: "single",
      options: [
        { value: "active", label: "Active" },
        { value: "retired", label: "Retired" },
      ],
    },
  ];
  const company = filters.values.company?.[0];
  const state = filters.values.state?.[0];
  const shown = ordered(
    rows.filter(
      (r) =>
        (!company || r.legal_entity_name === company) &&
        (!state || (state === "retired") === retired.has(r.id)) &&
        matches(filters.search, r.name, r.legal_entity_name, r.jurisdiction, r.grid_region),
    ),
    sort,
    {
      site: (r) => r.name,
      company: (r) => r.legal_entity_name,
      region: (r) => `${r.jurisdiction} ${r.grid_region ?? ""}`,
    },
  );

  const columns: GridColumn<FacilityRow>[] = [
    {
      id: "site",
      header: "Site",
      sortable: true,
      cell: (r) => (
        <EntityLink
          kind="site"
          to="/organisation/sites/$facilityId"
          params={{ facilityId: r.id }}
          search={{ ...keepSearch(screen) }}
          announceKind={false}
          testId={`site-page-${r.id}`}
        >
          {r.name}
        </EntityLink>
      ),
    },
    { id: "company", header: "Company", sortable: true, cell: (r) => r.legal_entity_name },
    {
      id: "region",
      header: "Region",
      sortable: true,
      cell: (r) => (r.grid_region ? `${r.jurisdiction} · ${r.grid_region}` : r.jurisdiction),
    },
    {
      id: "lease",
      header: "Lease",
      cell: (r) =>
        r.lease_type === "unknown" ? (
          <StatusChip tone="warn">Not answered</StatusChip>
        ) : (
          <>
            {LEASE_LABEL[r.lease_type] ?? r.lease_type}
            {/* Under operational control, whether the company operates a leased site is the fact that
                decides it (ADR-0034), so an unanswered one is said beside the lease. */}
            {["operating_lease", "occupied_under_licence"].includes(r.lease_type) && r.operates == null ? (
              <>
                {" "}
                <StatusChip tone="warn" testId="control-unanswered">
                  Control not answered
                </StatusChip>
              </>
            ) : null}
          </>
        ),
    },
    {
      id: "coverage",
      header: "Coverage",
      cell: (r) => {
        // A read still out is a bone and a failed one is said: "No sources set up" is a claim about
        // the site, and is only made from a census that answered (G-P9-09).
        if (coverage.isPending && window) return <Skeleton width={96} height={10} />;
        if (coverage.error) return "Not read";
        const cover = siteCover.get(r.id);
        return coverageWords(cover?.tally.outstanding, cover?.source_count);
      },
    },
    {
      id: "state",
      header: "State",
      cell: (r) => {
        // Whether a site is retired is the tree's to say: a read still out is a bone (and not a dash that
        // reads as "no state"), and a failed one is said, as the Coverage column does (G-P9-09).
        if (!window) return "—";
        if (tree.isPending) return <Skeleton width={56} height={10} />;
        if (tree.error || !tree.data) return "Not read";
        return retired.has(r.id) ? (
          <StatusChip tone="neutral">Retired</StatusChip>
        ) : (
          <StatusChip tone="ok">Active</StatusChip>
        );
      },
    },
    ...(isAdmin
      ? [
          {
            id: "manage",
            header: "Actions",
            cell: (r: FacilityRow) => (
              <button
                type="button"
                className={styles.manage}
                data-testid={`manage-site-${r.id}`}
                // Named by the site: four rows of "Manage" are four identical names (WCAG 2.4.6).
                aria-label={`Manage ${r.name}`}
                onClick={() => setManaging(r.id)}
              >
                Manage
              </button>
            ),
          },
        ]
      : []),
  ];

  const open = (site: FacilityRow) =>
    void navigate({
      to: "/organisation/sites/$facilityId",
      params: { facilityId: site.id },
      search: (prev: Partial<ScreenSearch>) => keepSearch(prev),
    });
  const managed = rows.find((r) => r.id === managing);
  const failed =
    facilities.error ?? entities.error ?? (window ? (tree.error ?? coverage.error) : null);
  const withGap = (coverage.data?.sites ?? []).filter((s) => s.tally.outstanding > 0).length;
  const nothingYet = facilities.data !== undefined && rows.length === 0;
  const noCompany = entities.data !== undefined && entities.data.length === 0;

  return (
    <>
      <TablePage
        icon={Buildings}
        title="Facilities"
        label={label}
        nothingYet={nothingYet}
        actions={
          <>
            <InfoPopover infoKey="facilities.sites" />
            <AddImportActions
              // A site belongs to a company: with none, Add site opens a drawer that says so, which
              // is a control that predictably fails. The empty state below offers the company first.
              role={isAdmin && !noCompany ? "admin" : undefined}
              addLabel="Add site"
              testId="open-add-site"
              onAdd={() => setAdding(true)}
              importLabel="Register many from a file"
              onImport={() =>
                void navigate({
                  to: "/organisation/structure",
                  search: (prev: Partial<ScreenSearch>) => keepSearch(prev),
                })
              }
            />
          </>
        }
        extra={
          isAdmin ? undefined : (
            <p className={styles.who} data-testid="facilities-who-can">
              An Admin sets up companies, sites and reporting periods.
            </p>
          )
        }
        band={
          facilities.data ? (
            <DashboardBand
              label="Facilities dashboard"
              tiles={
                <TileStrip label="Sites summary">
                  <Tile value={count(rows.length)} label="Sites" />
                  <Tile value={tree.data ? count(retired.size) : "—"} label="Retired" read={tree} />
                  <Tile
                    value={coverage.data ? count(withGap) : "—"}
                    label="With a gap"
                    infoKey="facilities.gap"
                    read={coverage}
                  />
                </TileStrip>
              }
              charts={
                !window
                  ? undefined
                  : [
                <CoverageCard
                  key="coverage"
                  read={coverage}
                  rollup={coverage.data}
                  onOpenSource={(sourceId) =>
                    void navigate({
                      to: "/sources/$sourceId",
                      params: { sourceId },
                      search: (prev: Partial<ScreenSearch>) => keepSearch(prev),
                    })
                  }
                  more={
                    <Link
                      to="/processing"
                      search={(prev: Partial<ScreenSearch>) => ({ ...keepSearch(prev), tab: "missing" })}
                    >
                      All sources
                    </Link>
                  }
                />,
                <SiteTotalsCard
                  key="totals"
                  read={tree}
                  tree={tree.data}
                  onOpen={(key) => screen.openBreakdown(key)}
                />,
                  ]
              }
            />
          ) : undefined
        }
        filters={
          nothingYet ? undefined : (
            <FilterLine
              filters={defs}
              state={filters}
              onChange={setFilters}
              searchLabel="Search sites"
              resultCount={facilities.data ? { shown: shown.length, total: rows.length, noun: "sites" } : undefined}
            />
          )
        }
        table={
          facilities.data ? (
            <GridTable
              caption="Sites"
              columns={columns}
              rows={shown}
              rowKey={(r) => r.id}
              rowLabel={(r) => r.name}
              sort={sort}
              onSortChange={setSort}
              onRowOpen={open}
              empty={
                nothingYet ? (
                  <EmptyState
                    icon={Buildings}
                    title="No sites yet"
                    compact
                    testId="sites-empty"
                    action={
                      noCompany ? (
                        <EmptyAction
                          label="Add a company"
                          allowed={isAdmin}
                          reason="An Admin adds companies and sites."
                          to="/facilities"
                          search={(prev: Partial<ScreenSearch>) => ({ ...keepSearch(prev), tab: "companies" })}
                          testId="sites-empty-company"
                        />
                      ) : (
                        <EmptyAction
                          label="Add a site"
                          allowed={isAdmin}
                          reason="An Admin adds companies and sites."
                          onClick={() => setAdding(true)}
                          testId="sites-empty-add"
                        />
                      )
                    }
                  >
                    A site is a place that uses energy or fuel.
                  </EmptyState>
                ) : undefined
              }
              testId="sites-table"
            />
          ) : undefined
        }
        state={
          failed ? (
            <ReadFailed
              of="The sites"
              error={failed}
              onRetry={() => {
                void facilities.refetch();
                void entities.refetch();
                void tree.refetch();
                void coverage.refetch();
              }}
            />
          ) : !facilities.data ? (
            <GridTable
              caption="Sites"
              columns={[]}
              rows={[]}
              rowKey={(r: FacilityRow) => r.id}
              loading
              testId="sites-loading"
            />
          ) : undefined
        }
      />
      <Drawer
        open={adding}
        onClose={() => {
          setAdding(false);
          adder.clear();
        }}
        title="Add site"
        width="md"
      >
        {companies.length === 0 ? (
          <EmptyState title="Add a company first" compact testId="sites-need-company" />
        ) : (
          <FacilityForm entities={companies} onSubmit={adder.report} />
        )}
        {adder.notes}
      </Drawer>
      <Drawer
        open={managed !== undefined}
        onClose={() => {
          setManaging(null);
          manager.clear();
        }}
        title={managed?.name ?? "Site"}
        width="lg"
      >
        {managed ? (
          <div className={styles.stack}>
            <SiteCorrection
              siteId={managed.id}
              entities={companies}
              onSubmit={manager.report}
              onDone={() => undefined}
            />
            <LeaseTreatment site={managed} onSubmit={manager.report} />
            <SitePanel siteId={managed.id} siteName={managed.name} onSubmit={manager.report} />
            {manager.notes}
          </div>
        ) : null}
      </Drawer>
    </>
  );
}
